import { useContext, useEffect, useRef } from 'react';
import { Text } from '@fluentui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { EditItem, useFetch } from '@moko/ui';
import { getApiErrorMessage } from '@moko/utils';
import AppContext from '../AppContext';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  createCompanyHoliday,
  getCompanyHoliday,
  getHolidayRegions,
  updateCompanyHoliday,
} from '../../services/companyHolidayService';
import { companyHolidayBody, toHolidayFormValues } from './companyHolidayBody';
import { companyHolidaysPath } from './pricingSettings';

const CompanyHolidayForm = () => {
  const isSaved = useRef(false);
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const holidayId = Number.parseInt(query.get('id') ?? '');
  const isEdit = !Number.isNaN(holidayId) && holidayId > 0;
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const context = useContext(AppContext);
  const title = isEdit ? 'Edit holiday' : 'Add holiday';

  useEffect(() => {
    context.setSelectedTab('Global settings');
  }, []);

  const regionFetch = useFetch<string[]>({
    queryKey: ['holidayRegions'],
    queryFn: getHolidayRegions,
    onError: (error) =>
      setErrorMessage(
        getApiErrorMessage(error, 'Failed to load holiday regions.')
      ),
  });

  const save = async (
    abortController: AbortController,
    item: Record<string, unknown>,
    id?: number
  ) => {
    const result = companyHolidayBody(item);
    if (!result.ok) {
      setErrorMessage(result.error);
      throw new Error(result.error);
    }
    try {
      if (id == null) {
        await createCompanyHoliday(abortController, result.body);
        setSuccessMessage(`Holiday ${result.body.name} created.`);
      } else {
        await updateCompanyHoliday(abortController, id, result.body);
        setSuccessMessage(`Holiday ${result.body.name} updated.`);
      }
      navigate(companyHolidaysPath);
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'Failed to save holiday.'));
      throw error;
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <Text variant="xLarge">{title}</Text>
      <EditItem
        getAction={async (abortController, id) =>
          toHolidayFormValues(await getCompanyHoliday(abortController, id))
        }
        newAction={(abortController, item) => save(abortController, item)}
        editAction={(abortController, id, item) =>
          save(abortController, item, id)
        }
        isSaved={isSaved}
        back={companyHolidaysPath}
        confirmOnCancel={false}
        onCancel={() => navigate(companyHolidaysPath)}
        defaultValues={isEdit ? undefined : JSON.stringify({ isActive: true })}
        metadata={{
          fields: [
            {
              name: 'name',
              fieldType: 'String',
              label: 'Name',
              mandatory: true,
            },
            {
              name: 'holidayDate',
              fieldType: 'Date',
              label: 'Date',
              mandatory: true,
            },
            {
              name: 'region',
              fieldType: 'String',
              label: 'Region',
              placeholder: 'Australia or Australia/NSW',
              lookupList: 'HolidayRegions',
              mandatory: true,
            },
          ],
          lookups: [
            {
              name: 'HolidayRegions',
              values: (regionFetch.data ?? []).map((region) => ({
                key: region,
                value: region,
              })),
            },
          ],
          validations: [
            {
              field: 'name',
              rules: [{ rule: 'NE', errorMessage: 'Name is required.' }],
            },
            {
              field: 'holidayDate',
              rules: [{ rule: 'NE', errorMessage: 'Date is required.' }],
            },
            {
              field: 'region',
              rules: [{ rule: 'NE', errorMessage: 'Region is required.' }],
            },
          ],
          interactions: [],
        }}
      />
    </div>
  );
};

export default CompanyHolidayForm;
